import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
    useGetGemsRewardConfigQuery,
    useUpdateGemsRewardConfigMutation,
    useGetGemsOverviewQuery,
    useGetGemsUserActivityQuery,
    useGetGemsTopUsersQuery,
    useGetGemsExchangeRateActiveQuery,
    useGetGemsExchangeRateHistoryQuery,
    useSetGemsExchangeRateMutation,
    usePollGemsReportExportMutation,
    useSubmitGemsReportExportMutation,
} from "../../../../redux/apiSlice";
import { Card, CardBody, CardHeader } from "../../../../_metronic/_partials/controls";
import ReactApexChart from "react-apexcharts";
import { Form as FormLayout, Modal } from "react-bootstrap";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import cogoToast from "cogo-toast";
import { SubHeader } from "../../Common/layout/SubHeader";
import TextInput from "../../Common/components/text-input";
import { downloadFile, errorMessage, formatDate, formatStatus, formatUsername } from "../../../util/Helpers";
import { rewardTypeOptions } from "../constants/rewardTypes";

const todayStr = new Date().toISOString().split("T")[0];

const formatMoney = (currency, value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "—";
    return `${currency || "NGN"} ${numericValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const formatRewardTypeLabel = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "Unknown";

    return raw
        .split("_")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
};

const getScrollableChartWidth = (points, { minWidth = 640, pxPerPoint = 70 } = {}) => {
    const count = Number(points || 0);
    if (!Number.isFinite(count) || count <= 0) return minWidth;
    return Math.max(minWidth, count * pxPerPoint);
};

const getTimeSeriesTimestamp = (point) => {
    const candidates = [
        point?.periodStart,
        point?.periodKey,
        point?.date,
        point?.created,
        point?.periodLabel,
    ];

    for (const candidate of candidates) {
        if (!candidate) continue;
        const timestamp = new Date(candidate).getTime();
        if (Number.isFinite(timestamp)) return timestamp;
    }

    return 0;
};

const getExchangeHistoryTimestamp = (entry) => {
    const candidates = [entry?.effectiveAt, entry?.created, entry?.updated];

    for (const candidate of candidates) {
        if (!candidate) continue;
        const timestamp = new Date(candidate).getTime();
        if (Number.isFinite(timestamp)) return timestamp;
    }

    return 0;
};

const buildChartColors = (count) =>
    Array.from({ length: count }, (_, index) => {
        const hue = Math.round((index * 137.508) % 360);
        return `hsl(${hue}, 72%, 56%)`;
    });

const waitFor = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getReportJobId = (response) => {
    const payload = response?.data ?? response ?? null;
    const inner = payload?.data ?? payload ?? null;
    return inner?.jobId ?? payload?.jobId ?? inner?.data?.jobId ?? null;
};

// Rows per page requested from core's export (core's maximum).
const REPORT_PAGE_SIZE = 5000;

const getReportPagination = (response) => {
    const payload = response?.payload ?? response ?? null;
    return payload?.data?.pagination ?? payload?.pagination ?? payload?.data?.data?.pagination ?? {};
};

const getReportRows = (response) => {
    const payload = response?.payload ?? response ?? null;
    const candidates = [
        payload?.data?.rows,
        payload?.rows,
        payload?.data?.data?.rows,
        payload?.data,
        payload,
    ];

    for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
    }

    return [];
};

const normalizeFileCell = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    if (value instanceof Date) return value.toISOString();
    try {
        return JSON.stringify(value);
    } catch (error) {
        return String(value);
    }
};

const buildDelimitedReport = (rows, format = "csv") => {
    if (!Array.isArray(rows) || rows.length === 0) return "";

    const headers = Array.from(
        rows.reduce((set, row) => {
            Object.keys(row || {}).forEach((key) => set.add(key));
            return set;
        }, new Set())
    );

    const separator = format === "xlsx" ? "\t" : ",";
    const escapeValue = (value) => `"${normalizeFileCell(value).replace(/"/g, '""')}"`;
    const lines = [
        headers.map(escapeValue).join(separator),
        ...rows.map((row) => headers.map((header) => escapeValue(row?.[header])).join(separator)),
    ];

    return lines.join("\n");
};

const downloadReportRows = ({ rows, reportType, format }) => {
    const safeFormat = format === "xlsx" ? "xlsx" : "csv";
    const extension = safeFormat === "xlsx" ? "xls" : "csv";
    const fileType = safeFormat === "xlsx" ? "application/vnd.ms-excel" : "text/csv;charset=utf-8;";
    const fileName = `gems-${reportType || "report"}-${new Date().toISOString().slice(0, 10)}.${extension}`;

    downloadFile({
        data: buildDelimitedReport(rows, safeFormat),
        fileName,
        fileType,
    });
};

const AxisGuide = ({ maxValue, label }) => {
    const max = Number(maxValue || 0);
    const safeMax = Number.isFinite(max) && max > 0 ? max : 0;
    const mid = safeMax / 2;
    return (
        <div className="d-flex flex-column justify-content-between pr-3" style={{ width: 84, height: 260 }}>
            <div className="text-muted font-size-sm">{label}</div>
            <div className="d-flex flex-column justify-content-between flex-grow-1 mt-2">
                <div className="text-muted font-size-sm text-right">{safeMax.toLocaleString()}</div>
                <div className="text-muted font-size-sm text-right">{mid ? mid.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "0"}</div>
                <div className="text-muted font-size-sm text-right">0</div>
            </div>
        </div>
    );
};

const rewardConfigTypes = [
    { value: "trade", label: "Trade" },
    { value: "savings", label: "Savings" },
    { value: "course_completion", label: "Course completion" },
    { value: "social_first_post", label: "Social first post" },
];

const rewardConfigFieldMap = {
    trade: [
        {
            name: "commissionPercentRegular",
            label: "Commission percent (regular)*",
            helperText: "Percentage of regular trade commission converted to Gems.",
        },
        {
            name: "commissionPercentPremium",
            label: "Commission percent (premium)*",
            helperText: "Percentage of premium trade commission converted to Gems.",
        },
    ],
    savings: [
        {
            name: "cashbackPercent",
            label: "Cashback percent*",
            helperText: "Percentage cashback applied for savings rewards.",
        },
    ],
    course_completion: [
        {
            name: "flatAmount",
            label: "Flat amount*",
            helperText: "Fixed Gems amount awarded for completing a course.",
        },
    ],
    social_first_post: [
        {
            name: "flatAmount",
            label: "Flat amount*",
            helperText: "Fixed Gems amount awarded for the first social post.",
        },
    ],
};

const getRewardConfigFields = (rewardType) => rewardConfigFieldMap[rewardType] || rewardConfigFieldMap.social_first_post;

const getNumericConfigValue = (config, keys, fallback = 0) => {
    for (const key of keys) {
        const value = Number(config?.[key]);
        if (Number.isFinite(value)) return value;
    }

    return fallback;
};

const formatConfigNumber = (value) => Number(value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const getRewardConfigSummary = (config) => {
    if (!config) return "—";

    if (config.rewardType === "trade") {
        return `Regular: ${formatConfigNumber(config.commissionPercentRegular)}% | Premium: ${formatConfigNumber(config.commissionPercentPremium)}%`;
    }

    if (config.rewardType === "savings") {
        return `Cashback: ${formatConfigNumber(config.cashbackPercent)}%`;
    }

    return `${formatConfigNumber(config.flatAmount)} Gems`;
};

const toBoolean = (value) => value === true || value === "true" || value === 1 || value === "1";

const normalizeRewardConfigList = (payload) => {
    const raw = payload?.data ?? payload?.rows ?? payload ?? {};

    const extractCandidate = (value) => {
        const candidate = value?.config ?? value?.settings ?? value ?? null;
        return candidate && typeof candidate === "object" && !Array.isArray(candidate) ? candidate : {};
    };

    const getEntry = (rewardType) => {
        if (Array.isArray(raw)) {
            const matched = raw.find(
                (item) => (item?.rewardType ?? item?.reward_type ?? item?.type) === rewardType
            );
            return extractCandidate(matched);
        }

        const collections = [
            raw,
            raw?.rules,
            raw?.config,
            raw?.configs,
            raw?.rewardConfig,
            raw?.rewardConfigs,
        ].filter(Boolean);

        for (const collection of collections) {
            if (Array.isArray(collection)) {
                const matched = collection.find(
                    (item) => (item?.rewardType ?? item?.reward_type ?? item?.type) === rewardType
                );
                if (matched) return extractCandidate(matched);
            }
        }

        for (const collection of collections) {
            if (!Array.isArray(collection) && collection?.[rewardType] !== undefined) {
                return extractCandidate(collection[rewardType]);
            }
        }

        return {};
    };

    return rewardConfigTypes.map((item) => {
        const config = getEntry(item.value);
        const flatAmount = getNumericConfigValue(config, ["flatAmount", "flat_amount"]);
        const cashbackPercent = getNumericConfigValue(config, ["cashbackPercent", "cashback_percent"]);
        const commissionPercentRegular = getNumericConfigValue(config, ["commissionPercentRegular", "commission_percent_regular"]);
        const commissionPercentPremium = getNumericConfigValue(config, ["commissionPercentPremium", "commission_percent_premium"]);

        return {
            rewardType: item.value,
            label: item.label,
            enabled: toBoolean(config?.enabled),
            flatAmount,
            cashbackPercent,
            commissionPercentRegular,
            commissionPercentPremium,
            rawConfig: config,
        };
    });
};

const SetExchangeRateModal = ({ show, onHide, userGroup, activeRate }) => {
    const [setGemsExchangeRate, { isLoading }] = useSetGemsExchangeRateMutation();

    const initialValues = useMemo(
        () => ({
            rate: activeRate?.rate ?? "",
            currency: activeRate?.currency ?? "NGN",
            note: activeRate?.meta?.note ?? "",
        }),
        [activeRate?.currency, activeRate?.meta?.note, activeRate?.rate]
    );

    const onSubmit = ({ rate, currency, note }) => {
        setGemsExchangeRate({
            rate: Number(rate),
            userGroup,
            currency,
            meta: note ? { note } : undefined,
        })
            .unwrap()
            .then(() => {
                cogoToast.success("Exchange rate updated");
                onHide();
            })
            .catch((err) => {
                cogoToast.error(errorMessage(err));
            });
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            aria-labelledby="contained-modal-title-vcenter"
            backdrop={isLoading ? "static" : true}
            keyboard={!isLoading}
        >
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={Yup.object({
                    rate: Yup.number().required("required"),
                    currency: Yup.string().required("required"),
                })}
                onSubmit={(values) => onSubmit(values)}
            >
                <Form>
                    <Modal.Header>
                        <Modal.Title id="contained-modal-title-vcenter">Set exchange rate</Modal.Title>
                        <button type="button" className="close" onClick={onHide} disabled={isLoading}>
                            <span aria-hidden="true" className="d-block">
                                ×
                            </span>
                            <span className="sr-only">Close</span>
                        </button>
                    </Modal.Header>
                    <Modal.Body style={{ maxHeight: "calc(100vh - 300px)", overflow: "auto" }}>
                        <FormLayout.Row>
                            <TextInput
                                label="Rate*"
                                name="rate"
                                type="number"
                                step="any"
                                shouldShowError={true}
                            />
                        </FormLayout.Row>
                        <FormLayout.Row>
                            <TextInput
                                label="Currency*"
                                name="currency"
                                type="text"
                                shouldShowError={true}
                            />
                        </FormLayout.Row>
                        <FormLayout.Row>
                            <TextInput
                                label="Note"
                                name="note"
                                type="text"
                                shouldShowError={true}
                            />
                        </FormLayout.Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <button
                            type="button"
                            onClick={onHide}
                            disabled={isLoading}
                            className="btn btn-light btn-elevate px-4"
                        >
                            Cancel
                        </button>
                        <button type="submit" disabled={isLoading} className="btn btn-primary btn-elevate px-4 ml-4">
                            Save
                        </button>
                    </Modal.Footer>
                </Form>
            </Formik>
        </Modal>
    );
};

const RewardConfigModal = ({ show, onHide, userGroup, rewardConfig }) => {
    const [updateGemsRewardConfig, { isLoading }] = useUpdateGemsRewardConfigMutation();
    const configFields = useMemo(
        () => getRewardConfigFields(rewardConfig?.rewardType),
        [rewardConfig?.rewardType]
    );

    const initialValues = useMemo(
        () => ({
            enabled: Boolean(rewardConfig?.enabled),
            flatAmount: rewardConfig?.flatAmount ?? 0,
            cashbackPercent: rewardConfig?.cashbackPercent ?? 0,
            commissionPercentRegular: rewardConfig?.commissionPercentRegular ?? 0,
            commissionPercentPremium: rewardConfig?.commissionPercentPremium ?? 0,
        }),
        [
            rewardConfig?.cashbackPercent,
            rewardConfig?.commissionPercentPremium,
            rewardConfig?.commissionPercentRegular,
            rewardConfig?.enabled,
            rewardConfig?.flatAmount,
        ]
    );

    const validationSchema = useMemo(() => {
        const shape = {
            enabled: Yup.boolean().required("required"),
        };

        configFields.forEach((field) => {
            shape[field.name] = Yup.number().min(0, "must be 0 or more").required("required");
        });

        return Yup.object(shape);
    }, [configFields]);

    const onSubmit = (values) => {
        if (!rewardConfig?.rewardType) return;

        const baseConfig = { ...(rewardConfig?.rawConfig || {}) };
        delete baseConfig.flatAmount;
        delete baseConfig.flat_amount;
        delete baseConfig.cashbackPercent;
        delete baseConfig.cashback_percent;
        delete baseConfig.commissionPercentRegular;
        delete baseConfig.commission_percent_regular;
        delete baseConfig.commissionPercentPremium;
        delete baseConfig.commission_percent_premium;

        const nextConfig = {
            ...baseConfig,
            enabled: values.enabled,
        };

        configFields.forEach((field) => {
            nextConfig[field.name] = Number(values[field.name]);
        });

        updateGemsRewardConfig({
            rewardType: rewardConfig.rewardType,
            userGroup,
            config: nextConfig,
        })
            .unwrap()
            .then(() => {
                cogoToast.success(`${rewardConfig.label} reward config updated`);
                onHide();
            })
            .catch((err) => {
                cogoToast.error(errorMessage(err));
            });
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            aria-labelledby="reward-config-modal-title"
            backdrop={isLoading ? "static" : true}
            keyboard={!isLoading}
        >
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values) => onSubmit(values)}
            >
                {({ values, setFieldValue }) => (
                    <Form>
                        <Modal.Header>
                            <Modal.Title id="reward-config-modal-title">
                                Update {rewardConfig?.label || "reward"} config
                            </Modal.Title>
                            <button type="button" className="close" onClick={onHide} disabled={isLoading}>
                                <span aria-hidden="true" className="d-block">
                                    ×
                                </span>
                                <span className="sr-only">Close</span>
                            </button>
                        </Modal.Header>
                        <Modal.Body style={{ maxHeight: "calc(100vh - 300px)", overflow: "auto" }}>
                            <div className="text-muted mb-5">
                                Configure whether this reward type is enabled and update the values used for this reward rule.
                            </div>
                            <FormLayout.Group className="mb-5">
                                <FormLayout.Check
                                    type="switch"
                                    id={`reward-config-enabled-${rewardConfig?.rewardType || "item"}`}
                                    label="Enabled"
                                    checked={Boolean(values.enabled)}
                                    onChange={(event) => setFieldValue("enabled", event.target.checked)}
                                />
                            </FormLayout.Group>
                            {configFields.map((field) => (
                                <FormLayout.Row key={field.name}>
                                    <TextInput
                                        label={field.label}
                                        name={field.name}
                                        type="number"
                                        step="any"
                                        shouldShowError={true}
                                    />
                                    {field.helperText ? (
                                        <div className="text-muted font-size-sm mt-1">
                                            {field.helperText}
                                        </div>
                                    ) : null}
                                </FormLayout.Row>
                            ))}
                        </Modal.Body>
                        <Modal.Footer>
                            <button
                                type="button"
                                onClick={onHide}
                                disabled={isLoading}
                                className="btn btn-light btn-elevate px-4"
                            >
                                Cancel
                            </button>
                            <button type="submit" disabled={isLoading} className="btn btn-primary btn-elevate px-4 ml-4">
                                {isLoading ? "Saving..." : "Save"}
                            </button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

const GemsOverview = () => {
    const { userGroup } = useParams();
    const [showSetExchangeRateModal, setShowSetExchangeRateModal] = useState(false);
    const [selectedRewardConfig, setSelectedRewardConfig] = useState(null);
    const [submitGemsReportExport, { isLoading: isGeneratingReport }] = useSubmitGemsReportExportMutation();
    const [pollGemsReportExport] = usePollGemsReportExportMutation();
    const [isDownloadingReport, setIsDownloadingReport] = useState(false);

    const [filters, setFilters] = useState({
        startDate: "",
        endDate: todayStr,
    });

    const [overviewFilters, setOverviewFilters] = useState({
        period: "",
        rewardTypes: [],
    });

    const [reportFilters, setReportFilters] = useState({
        startDate: "",
        endDate: todayStr,
        reportType: "overview",
        rewardTypes: [],
        statuses: [],
        format: "csv",
    });
    const [conversionGems, setConversionGems] = useState("");

    const commonArgs = useMemo(
        () => ({
            userGroup,
            startDate: filters.startDate || "",
            endDate: filters.endDate || "",
        }),
        [userGroup, filters.startDate, filters.endDate]
    );

    const overviewArgs = useMemo(
        () => ({
            ...commonArgs,
            period: overviewFilters.period || "",
            rewardTypes: overviewFilters.rewardTypes,
        }),
        [commonArgs, overviewFilters.period, overviewFilters.rewardTypes]
    );

    const { data: overviewResponse, isFetching: isFetchingOverview } = useGetGemsOverviewQuery(overviewArgs);
    const { data: userActivityResponse } = useGetGemsUserActivityQuery({
        ...commonArgs,
        rewardTypes: overviewFilters.rewardTypes,
    });
    const { data: topUsersData } = useGetGemsTopUsersQuery({ ...commonArgs, limit: 10 });
    const { data: activeExchangeRate, isFetching: isFetchingActiveExchangeRate } = useGetGemsExchangeRateActiveQuery(
        { userGroup }
    );
    const { data: exchangeRateHistory, isFetching: isFetchingExchangeRateHistory } = useGetGemsExchangeRateHistoryQuery(
        { userGroup }
    );
    const { data: rewardConfigResponse, isFetching: isFetchingRewardConfig } = useGetGemsRewardConfigQuery(
        { userGroup },
        { skip: !userGroup }
    );

    const onFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const onOverviewFilterChange = (e) => {
        const { name, value } = e.target;

        if (name === "rewardTypes") {
            setOverviewFilters((prev) => ({ ...prev, rewardTypes: value ? [value] : [] }));
            return;
        }

        setOverviewFilters((prev) => ({ ...prev, [name]: value }));
    };

    const onReportFilterChange = (e) => {
        const { name, value } = e.target;
        if (name === "rewardTypes" || name === "statuses") {
            setReportFilters((prev) => ({ ...prev, [name]: value ? [value] : [] }));
            return;
        }
        setReportFilters((prev) => ({ ...prev, [name]: value }));
    };

    const onGenerateReport = () => {
        const safeReportType = reportFilters.reportType || "overview";
        const safeFormat = reportFilters.format || "csv";

        const runExport = async () => {
            try {
                setIsDownloadingReport(true);
                cogoToast.info(`Submitting ${safeFormat.toUpperCase()} Gems export...`);

                const submitResponse = await submitGemsReportExport({
                    userGroup,
                    startDate: reportFilters.startDate,
                    endDate: reportFilters.endDate,
                    reportType: safeReportType,
                    rewardTypes: reportFilters.rewardTypes,
                    statuses: reportFilters.statuses,
                    format: safeFormat,
                    sendEmail: false,
                }).unwrap();

                const jobId = getReportJobId(submitResponse);
                if (!jobId) {
                    throw new Error("Unable to read Gems export jobId from submit response.");
                }

                // 1) Wait for core to finish building the export (202 = still running).
                const maxAttempts = 30;
                let firstPage = null;
                for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
                    const pollResponse = await pollGemsReportExport({ jobId, offset: 0, limit: REPORT_PAGE_SIZE }).unwrap();
                    if (pollResponse?.status === 200) {
                        firstPage = pollResponse;
                        break;
                    }
                    await waitFor(2000);
                }
                if (!firstPage) {
                    throw new Error("Gems export timed out while waiting for the report to finish.");
                }

                // 2) Collect every page: while pagination.hasMore is true, fetch the
                //    next page from pagination.nextOffset (limit 5000).
                const rows = [...getReportRows(firstPage)];
                let pagination = getReportPagination(firstPage);
                let offset = 0;
                while (pagination?.hasMore === true) {
                    const returned = Number(pagination.returned) > 0 ? Number(pagination.returned) : REPORT_PAGE_SIZE;
                    const nextOffset = Number.isInteger(pagination.nextOffset) ? pagination.nextOffset : offset + returned;
                    if (nextOffset <= offset) {
                        throw new Error("Gems export returned an invalid next page.");
                    }
                    offset = nextOffset;
                    if (pagination.total) {
                        cogoToast.info(`Fetching Gems rows ${offset + 1}–${Math.min(offset + REPORT_PAGE_SIZE, pagination.total)} of ${pagination.total}...`);
                    }
                    const pageResponse = await pollGemsReportExport({ jobId, offset, limit: REPORT_PAGE_SIZE }).unwrap();
                    const pageRows = getReportRows(pageResponse);
                    if (!pageRows.length) break;
                    rows.push(...pageRows);
                    pagination = getReportPagination(pageResponse);
                }

                if (!rows.length) {
                    throw new Error("Gems export completed but returned no rows.");
                }

                downloadReportRows({
                    rows,
                    reportType: safeReportType,
                    format: safeFormat,
                });
                cogoToast.success(`Gems ${safeFormat.toUpperCase()} report downloaded (${rows.length.toLocaleString()} rows).`);
            } catch (err) {
                cogoToast.error(errorMessage(err));
            } finally {
                setIsDownloadingReport(false);
            }
        };

        runExport();
    };

    const overview = useMemo(() => {
        const payload = overviewResponse?.data ?? overviewResponse ?? null;
        return payload && typeof payload === "object" ? payload : {};
    }, [overviewResponse]);
    const userActivity = useMemo(() => {
        const payload = userActivityResponse?.data ?? userActivityResponse ?? null;
        return payload && typeof payload === "object" ? payload : {};
    }, [userActivityResponse]);

    const activityCards = useMemo(() => {
        const totalUsersEarning = Number(userActivity?.totalUsersEarning ?? 0);
        const dailyActiveUsers = Number(userActivity?.dailyActiveUsers ?? 0);
        const weeklyActiveUsers = Number(userActivity?.weeklyActiveUsers ?? 0);
        const monthlyActiveUsers = Number(userActivity?.monthlyActiveUsers ?? 0);

        return [
            { label: "Users earning Gems", value: totalUsersEarning.toLocaleString(), valueClassName: "text-primary" },
            { label: "Daily active", value: dailyActiveUsers.toLocaleString(), valueClassName: "text-primary" },
            { label: "Weekly active", value: weeklyActiveUsers.toLocaleString(), valueClassName: "text-warning" },
            { label: "Monthly active", value: monthlyActiveUsers.toLocaleString(), valueClassName: "text-danger" },
        ];
    }, [
        userActivity?.dailyActiveUsers,
        userActivity?.monthlyActiveUsers,
        userActivity?.totalUsersEarning,
        userActivity?.weeklyActiveUsers,
    ]);
    const timeSeries = useMemo(
        () =>
            (Array.isArray(overview?.timeSeries) ? overview.timeSeries : [])
                .slice()
                .sort((a, b) => getTimeSeriesTimestamp(b) - getTimeSeriesTimestamp(a)),
        [overview?.timeSeries]
    );
    const [timeSeriesPage, setTimeSeriesPage] = useState(1);
    const [timeSeriesPageSize, setTimeSeriesPageSize] = useState(7);
    const timeSeriesTotalPages = useMemo(
        () => Math.max(1, Math.ceil(timeSeries.length / timeSeriesPageSize)),
        [timeSeries.length, timeSeriesPageSize]
    );
    useEffect(() => {
        setTimeSeriesPage(1);
    }, [filters.startDate, filters.endDate, overviewFilters.period, overviewFilters.rewardTypes, timeSeriesPageSize, userGroup]);

    const rewardConfigList = useMemo(
        () => normalizeRewardConfigList(rewardConfigResponse),
        [rewardConfigResponse]
    );
    useEffect(() => {
        if (timeSeriesPage > timeSeriesTotalPages) setTimeSeriesPage(timeSeriesTotalPages);
    }, [timeSeriesPage, timeSeriesTotalPages]);
    const pagedTimeSeries = useMemo(() => {
        const start = (Math.max(1, timeSeriesPage) - 1) * timeSeriesPageSize;
        return timeSeries.slice(start, start + timeSeriesPageSize);
    }, [timeSeries, timeSeriesPage, timeSeriesPageSize]);
    const topUsers = topUsersData?.topUsers || [];
    const activeRate = activeExchangeRate?.data || activeExchangeRate || null;
    const activeRateValue = Number(activeRate?.rate ?? 0);
    const activeRateCurrency = activeRate?.currency || "NGN";
    const gemsToCash = (gems) => {
        const numericGems = Number(gems);
        if (!Number.isFinite(numericGems) || !Number.isFinite(activeRateValue) || activeRateValue <= 0) return null;
        return numericGems * activeRateValue;
    };
    const conversionCashValue = gemsToCash(conversionGems);
    const cashToGems = (amount) => {
        const numericAmount = Number(amount);
        if (!Number.isFinite(numericAmount) || !Number.isFinite(activeRateValue) || activeRateValue <= 0) return null;
        return numericAmount / activeRateValue;
    };
    const rateHistory = useMemo(() => {
        const rows = Array.isArray(exchangeRateHistory?.data)
            ? exchangeRateHistory.data
            : Array.isArray(exchangeRateHistory?.rows)
                ? exchangeRateHistory.rows
                : Array.isArray(exchangeRateHistory)
                    ? exchangeRateHistory
                    : [];

        return rows.slice().sort((a, b) => getExchangeHistoryTimestamp(b) - getExchangeHistoryTimestamp(a));
    }, [exchangeRateHistory]);

    const engagementByActivity = useMemo(
        () => userActivity?.engagementByActivity || [],
        [userActivity?.engagementByActivity]
    );
    const engagementEntries = useMemo(() => {
        const allowedRewardTypes = new Set(rewardTypeOptions);
        const normalizedActivities = engagementByActivity
            .map((activity) => ({
                rewardType: activity?.rewardType || "unknown",
                totalAmount: Number(activity?.totalAmount ?? 0),
            }))
            .filter((activity) => allowedRewardTypes.has(activity.rewardType))
            .filter((activity) => Number.isFinite(activity.totalAmount))
            .sort((a, b) => b.totalAmount - a.totalAmount);

        const total = normalizedActivities.reduce((sum, activity) => sum + activity.totalAmount, 0);
        const colors = buildChartColors(normalizedActivities.length);

        return normalizedActivities.map((activity, index) => ({
            ...activity,
            label: formatRewardTypeLabel(activity.rewardType),
            percentage: total > 0 ? (activity.totalAmount / total) * 100 : 0,
            color: colors[index],
        }));
    }, [engagementByActivity]);
    const engagementSeries = useMemo(
        () => engagementEntries.map((entry) => entry.totalAmount),
        [engagementEntries]
    );
    const engagementLabels = useMemo(
        () => engagementEntries.map((entry) => entry.label),
        [engagementEntries]
    );
    const engagementTotal = useMemo(
        () => engagementEntries.reduce((sum, entry) => sum + entry.totalAmount, 0),
        [engagementEntries]
    );
    const engagementColors = useMemo(
        () => engagementEntries.map((entry) => entry.color),
        [engagementEntries]
    );
    const hasEngagementData = engagementSeries.length > 0;
    const engagementOptions = useMemo(
        () => ({
            labels: engagementLabels,
            legend: {
                show: false,
            },
            chart: {
                toolbar: { show: false },
            },
            stroke: {
                width: 3,
                colors: ["#FFFFFF"],
            },
            dataLabels: {
                enabled: false,
            },
            tooltip: {
                y: {
                    formatter: (value) => {
                        const numericValue = Number(value ?? 0);
                        const percentage = engagementTotal > 0 ? (numericValue / engagementTotal) * 100 : 0;
                        return `${numericValue.toLocaleString()} Gems (${percentage.toFixed(1)}%)`;
                    },
                },
            },
            plotOptions: {
                pie: {
                    expandOnClick: false,
                    customScale: 1.02,
                    donut: {
                        size: "76%",
                        labels: {
                            show: false,
                        },
                    },
                },
            },
            colors: engagementColors,
        }),
        [engagementColors, engagementLabels, engagementTotal]
    );
    const engagementInsights = useMemo(() => {
        if (!engagementEntries.length) return null;

        const selectedRewardType = overviewFilters.rewardTypes?.[0] || "";
        const topActivity = engagementEntries[0];
        const secondActivity = engagementEntries[1] || null;
        const smallestActivity = engagementEntries[engagementEntries.length - 1];
        const selectedActivity = selectedRewardType
            ? engagementEntries.find((activity) => activity.rewardType === selectedRewardType) || null
            : null;
        const selectedRank = selectedActivity
            ? engagementEntries.findIndex((activity) => activity.rewardType === selectedActivity.rewardType) + 1
            : null;

        return {
            totalEngagement: engagementTotal,
            activeTypes: engagementEntries.length,
            selectedRewardType,
            selectedActivity,
            selectedShare: selectedActivity?.percentage ?? 0,
            selectedRank,
            topActivity,
            secondActivity,
            smallestActivity,
            topShare: topActivity?.percentage ?? 0,
        };
    }, [engagementEntries, engagementTotal, overviewFilters.rewardTypes]);
    const engagementCenterDisplay = useMemo(() => {
        if (!engagementInsights) return null;

        if (engagementInsights.selectedActivity) {
            return {
                eyebrow: "Selected reward type",
                title: engagementInsights.selectedActivity.label,
                value: engagementInsights.selectedActivity.totalAmount.toLocaleString(),
                subtitle: `${engagementInsights.selectedShare.toFixed(1)}% of visible Gems`,
            };
        }

        return {
            eyebrow: "Visible total",
            title: "All reward types",
            value: engagementInsights.totalEngagement.toLocaleString(),
            subtitle: `${engagementInsights.activeTypes.toLocaleString()} reward types in view`,
        };
    }, [engagementInsights]);

    const timeSeriesLabels = useMemo(() => pagedTimeSeries.map((p) => p.periodLabel), [pagedTimeSeries]);
    const timeSeriesEarned = useMemo(() => pagedTimeSeries.map((p) => Number(p.totalEarned ?? 0)), [pagedTimeSeries]);
    const timeSeriesRedeemed = useMemo(() => pagedTimeSeries.map((p) => Number(p.totalRedeemed ?? 0)), [pagedTimeSeries]);
    const hasTimeSeriesChartData = timeSeriesEarned.some((v) => v > 0) || timeSeriesRedeemed.some((v) => v > 0);
    const timeSeriesChartWidth = useMemo(
        () => getScrollableChartWidth(timeSeriesLabels.length, { pxPerPoint: 110 }),
        [timeSeriesLabels.length]
    );
    const timeSeriesChartOptions = useMemo(
        () => ({
            chart: { toolbar: { show: false } },
            stroke: { curve: "smooth", width: 2 },
            dataLabels: { enabled: false },
            xaxis: {
                categories: timeSeriesLabels,
                labels: { rotate: -45, rotateAlways: false, hideOverlappingLabels: false, trim: false },
            },
            yaxis: { show: false },
            legend: { position: "top" },
            colors: ["#1BC5BD", "#F64E60"],
        }),
        [timeSeriesLabels]
    );
    const timeSeriesChartSeries = useMemo(
        () => [
            { name: "Earned", data: timeSeriesEarned },
            { name: "Redeemed", data: timeSeriesRedeemed },
        ],
        [timeSeriesEarned, timeSeriesRedeemed]
    );
    const timeSeriesTotals = useMemo(() => {
        return timeSeries.reduce(
            (acc, p) => {
                acc.earned += Number(p.totalEarned ?? 0);
                acc.redeemed += Number(p.totalRedeemed ?? 0);
                return acc;
            },
            { earned: 0, redeemed: 0 }
        );
    }, [timeSeries]);
    const timeSeriesTotalNet = timeSeriesTotals.earned - timeSeriesTotals.redeemed;

    return (
        <>
            {userGroup && (
                <SubHeader>
                    <SubHeader.Nav>
                        <SubHeader.NavItem label={"Overview"} to={`/gems/${userGroup}/overview`} />
                        <SubHeader.NavItem label={"Accruals"} to={`/gems/${userGroup}/overview/accruals`} />
                        <SubHeader.NavItem label={"Redemptions"} to={`/gems/${userGroup}/overview/redemptions`} />
                        <SubHeader.NavItem label={"Campaigns"} to={`/gems/${userGroup}/overview/campaigns`} />
                        <SubHeader.NavItem
                            label={"Reward attempts"}
                            to={`/gems/${userGroup}/overview/reward-attempts`}
                        />
                        <SubHeader.NavItem label={"Audit logs"} to={`/gems/${userGroup}/overview/audit-logs`} />
                    </SubHeader.Nav>
                </SubHeader>
            )}
            <div className="py-4 px-6">
                <div className="mb-4">
                    <h2 className="font-weight-bolder mb-1">Gems Dashboard</h2>
                    <span className="text-muted">
                        Monitor Gems accrual, redemption and engagement across campaigns and activities.
                    </span>
                </div>

                <div className="d-flex flex-wrap align-items-end mb-6">
                    <div className="mr-4 mb-3">
                    <span className="text-muted font-size-sm d-block mb-1">Start date</span>
                    <input
                        type="date"
                        className="form-control"
                        name="startDate"
                        value={filters.startDate}
                        onChange={onFilterChange}
                    />
                </div>
                <div className="mr-4 mb-3">
                    <span className="text-muted font-size-sm d-block mb-1">End date</span>
                    <input
                        type="date"
                        className="form-control"
                        name="endDate"
                        value={filters.endDate}
                        onChange={onFilterChange}
                    />
                    </div>
                    <div className="mb-3" style={{ minWidth: 280 }}>
                        <span className="text-muted font-size-sm d-block mb-1">Reward types</span>
                        <select
                            className="form-control"
                            name="rewardTypes"
                            value={overviewFilters.rewardTypes?.[0] || ""}
                            onChange={onOverviewFilterChange}
                        >
                            <option value="">All</option>
                            {rewardTypeOptions.map((t) => (
                                <option key={t} value={t}>
                                    {t}
                                </option>
                            ))}
                        </select>
                    </div>
                    {isFetchingOverview ? (
                        <div className="ml-4 mb-3 d-flex align-items-center">
                            <div className="spinner-border spinner-border-sm text-primary mr-2" role="status" />
                            <span className="text-muted font-size-sm">Updating overview...</span>
                        </div>
                    ) : null}
                </div>

                <div className="row mb-6">
                    <div className="col-md-12 mb-4">
                        <Card>
                            <CardHeader title="Engagement by activity" />
                            <CardBody>
                                {hasEngagementData ? (
                                    <div className="row align-items-start">
                                        <div className={engagementInsights ? "col-lg-7 mb-5 mb-lg-0" : "col-12"}>
                                            <div className="d-flex flex-column align-items-center">
                                                <div
                                                    className="position-relative d-flex align-items-center justify-content-center"
                                                    style={{ minHeight: 340, width: "100%" }}
                                                >
                                                    <div style={{ width: "100%", maxWidth: 420 }}>
                                                        <ReactApexChart
                                                            options={engagementOptions}
                                                            series={engagementSeries}
                                                            type="donut"
                                                            height={340}
                                                        />
                                                    </div>
                                                    {engagementCenterDisplay ? (
                                                        <div
                                                            className="position-absolute d-flex flex-column align-items-center justify-content-center text-center px-3"
                                                            style={{ width: 170, pointerEvents: "none" }}
                                                        >
                                                            <div className="text-muted font-size-xs text-uppercase mb-1">
                                                                {engagementCenterDisplay.eyebrow}
                                                            </div>
                                                            <div className="font-weight-bolder font-size-sm text-dark mb-1">
                                                                {engagementCenterDisplay.title}
                                                            </div>
                                                            <div className="font-weight-bolder font-size-h3 text-dark mb-1">
                                                                {engagementCenterDisplay.value}
                                                            </div>
                                                            <div className="text-muted font-size-sm">
                                                                {engagementCenterDisplay.subtitle}
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                </div>
                                                <div className="w-100 bg-light rounded p-3 mt-2" style={{ maxWidth: 540 }}>
                                                    <div className="font-weight-bolder mb-3">Breakdown by reward type</div>
                                                    <div style={{ maxHeight: 220, overflowY: "auto" }}>
                                                        {engagementEntries.map((entry) => {
                                                            const isSelected =
                                                                engagementInsights?.selectedRewardType &&
                                                                engagementInsights.selectedRewardType === entry.rewardType;

                                                            return (
                                                                <div
                                                                    key={entry.rewardType}
                                                                    className={`d-flex justify-content-between align-items-center rounded px-3 py-2 mb-2 ${
                                                                        isSelected ? "bg-white border border-primary" : "bg-white"
                                                                    }`}
                                                                >
                                                                    <div className="d-flex align-items-center pr-3">
                                                                        <span
                                                                            className="mr-3 rounded-circle"
                                                                            style={{
                                                                                width: 10,
                                                                                height: 10,
                                                                                backgroundColor: entry.color,
                                                                                flexShrink: 0,
                                                                            }}
                                                                        />
                                                                        <div>
                                                                            <div className="font-weight-bolder text-dark">
                                                                                {entry.label}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="text-right">
                                                                        <div className="font-weight-bolder text-dark">
                                                                            {entry.totalAmount.toLocaleString()}
                                                                        </div>
                                                                        <div className="text-muted font-size-sm">Gems</div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {engagementInsights ? (
                                            <div className="col-lg-5">
                                                <div className="bg-light rounded p-4 h-100">
                                                    <div className="font-weight-bolder mb-3">What changed in this view</div>
                                                    {engagementInsights.selectedRewardType ? (
                                                        <>
                                                            <div className="text-muted font-size-sm text-uppercase mb-2">Selected reward type</div>
                                                            <div className="font-weight-bolder">
                                                                {formatRewardTypeLabel(engagementInsights.selectedRewardType)}
                                                            </div>
                                                            {engagementInsights.selectedActivity ? (
                                                                <>
                                                                    <div className="row">
                                                                        <div className="col-6 mb-4">
                                                                            <div className="text-muted font-size-xs text-uppercase mb-1">Gems</div>
                                                                            <div className="font-weight-bolder">
                                                                                {engagementInsights.selectedActivity.totalAmount.toLocaleString()}
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-6 mb-4">
                                                                            <div className="text-muted font-size-xs text-uppercase mb-1">Value</div>
                                                                            <div className="font-weight-bolder">
                                                                                {gemsToCash(engagementInsights.selectedActivity.totalAmount) !== null
                                                                                    ? formatMoney(
                                                                                          activeRateCurrency,
                                                                                          gemsToCash(engagementInsights.selectedActivity.totalAmount)
                                                                                      )
                                                                                    : "—"}
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-6">
                                                                            <div className="text-muted font-size-xs text-uppercase mb-1">Rank</div>
                                                                            <div className="font-weight-bolder">
                                                                                #{engagementInsights.selectedRank}
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-6">
                                                                            <div className="text-muted font-size-xs text-uppercase mb-1">Gap To Top (Gems)</div>
                                                                            <div className="font-weight-bolder">
                                                                                {(
                                                                                    engagementInsights.topActivity.totalAmount -
                                                                                    engagementInsights.selectedActivity.totalAmount
                                                                                ).toLocaleString()}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="separator separator-dashed my-4" />
                                                                    <div className="text-muted font-size-sm">
                                                                        Out of {engagementInsights.activeTypes.toLocaleString()} visible reward types, the current leader is{" "}
                                                                        <span className="font-weight-bolder text-dark">
                                                                            {engagementInsights.topActivity.label}
                                                                        </span>{" "}
                                                                        with {engagementInsights.topActivity.totalAmount.toLocaleString()} Gems.
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <div className="text-muted font-size-sm mt-2">
                                                                        No engagement details are available for this reward type in the current filtered result.
                                                                    </div>
                                                                    <div className="separator separator-dashed my-4" />
                                                                    <div className="text-muted font-size-sm">
                                                                        Visible total: {engagementInsights.totalEngagement.toLocaleString()} Gems across{" "}
                                                                        {engagementInsights.activeTypes.toLocaleString()} activity types.
                                                                    </div>
                                                                </>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="text-muted font-size-sm text-uppercase mb-2">Overview</div>
                                                            <div className="font-weight-bolder">
                                                                {engagementInsights.totalEngagement.toLocaleString()} Gems across{" "}
                                                                {engagementInsights.activeTypes.toLocaleString()} activity types
                                                            </div>
                                                            <div className="separator separator-dashed my-4" />
                                                            <div className="row">
                                                                <div className="col-6 mb-4">
                                                                    <div className="text-muted font-size-xs text-uppercase mb-1">Top reward type</div>
                                                                    <div className="font-weight-bolder">
                                                                        {engagementInsights.topActivity.label}
                                                                    </div>
                                                                </div>
                                                                <div className="col-6 mb-4">
                                                                    <div className="text-muted font-size-xs text-uppercase mb-1">Value</div>
                                                                    <div className="font-weight-bolder">
                                                                        {gemsToCash(engagementInsights.totalEngagement) !== null
                                                                            ? formatMoney(
                                                                                  activeRateCurrency,
                                                                                  gemsToCash(engagementInsights.totalEngagement)
                                                                              )
                                                                            : "—"}
                                                                    </div>
                                                                </div>
                                                                <div className="col-6">
                                                                    <div className="text-muted font-size-xs text-uppercase mb-1">Next largest</div>
                                                                    <div className="font-weight-bolder">
                                                                        {engagementInsights.secondActivity
                                                                            ? engagementInsights.secondActivity.label
                                                                            : "None"}
                                                                    </div>
                                                                </div>
                                                                <div className="col-6">
                                                                    <div className="text-muted font-size-xs text-uppercase mb-1">Smallest</div>
                                                                    <div className="font-weight-bolder">
                                                                        {engagementInsights.smallestActivity.label}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                ) : (
                                    <div className="text-center text-muted py-5">
                                        No engagement data for selected period
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </div>
                </div>

                <div className="row mb-6">
                    <div className="col-lg-6 mb-6">
                        <Card>
                            <CardHeader
                                title="Exchange rate"
                                toolbar={
                                    <div className="d-flex align-items-center px-1 py-0">
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            onClick={() => setShowSetExchangeRateModal(true)}
                                        >
                                            Set exchange rate
                                        </button>
                                    </div>
                                }
                            />
                            <CardBody>
                                {isFetchingActiveExchangeRate ? (
                                    <div className="text-center text-muted py-5">Loading...</div>
                                ) : activeRate ? (
                                    <div className="d-flex flex-column">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <div className="d-flex flex-column">
                                                <span className="text-muted font-size-sm">Active rate</span>
                                                <span className="font-weight-bolder font-size-h4">
                                                    1 Gem = {Number(activeRate.rate ?? 0).toLocaleString()} {activeRate.currency || "—"}
                                                </span>
                                                {cashToGems(1) ? (
                                                    <span className="text-muted font-size-sm">
                                                        1 {activeRate.currency || "—"} = {Number(cashToGems(1)).toLocaleString(undefined, { maximumFractionDigits: 6 })} Gems
                                                    </span>
                                                ) : null}
                                            </div>
                                            <span className="badge badge-light-success">
                                                {formatStatus(activeRate.status || "active")}
                                            </span>
                                        </div>
                                        <div className="d-flex flex-wrap text-muted font-size-sm">
                                            <div className="mr-6 mb-1">
                                                Effective: {activeRate.effectiveAt ? formatDate(activeRate.effectiveAt) : "—"}
                                            </div>
                                            <div className="mb-1">Note: {activeRate?.meta?.note || "—"}</div>
                                        </div>
                                        <div className="d-flex align-items-center mt-4">
                                            <input
                                                type="number"
                                                className="form-control form-control-sm mr-2"
                                                value={conversionGems}
                                                onChange={(e) => setConversionGems(e.target.value)}
                                                placeholder="Gems"
                                                style={{ width: 140 }}
                                                min="0"
                                            />
                                            <span className="text-muted font-size-sm">
                                                {conversionGems
                                                    ? conversionCashValue !== null
                                                        ? `≈ ${formatMoney(activeRateCurrency, conversionCashValue)}`
                                                        : "—"
                                                    : `Convert to ${activeRateCurrency}`}
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center text-muted py-5">No active exchange rate</div>
                                )}

                                <div className="separator separator-dashed my-6"></div>

                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="font-weight-bold mb-0">History</h5>
                                </div>

                                <div className="table-responsive">
                                    <table className="table table-sm table-head-custom">
                                        <thead>
                                            <tr>
                                                <th>Effective</th>
                                                <th>Currency</th>
                                                <th className="text-right">Rate</th>
                                                <th>Status</th>
                                                <th>Note</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {isFetchingExchangeRateHistory ? (
                                                <tr>
                                                    <td colSpan={5} className="text-center text-muted py-5">
                                                        Loading...
                                                    </td>
                                                </tr>
                                            ) : rateHistory.length ? (
                                                rateHistory.slice(0, 6).map((r) => (
                                                    <tr key={r.id}>
                                                        <td>{r.effectiveAt ? formatDate(r.effectiveAt) : "—"}</td>
                                                        <td className="text-muted">{r.currency || "—"}</td>
                                                        <td className="text-right">
                                                            <div className="font-weight-bolder">
                                                                {Number(r.rate ?? 0).toLocaleString()}
                                                            </div>
                                                            <div className="text-muted font-size-sm">
                                                                {r.currency || "—"} per Gem
                                                            </div>
                                                        </td>
                                                        <td>{formatStatus(r.status || "")}</td>
                                                        <td className="text-muted" style={{ minWidth: 240 }}>
                                                            {r?.meta?.note || "—"}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={5} className="text-center text-muted py-5">
                                                        No exchange rate history
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </CardBody>
                        </Card>
                    </div>

                    <div className="col-lg-6 mb-6">
                        <Card>
                            <CardHeader
                                title="Gems over time"
                                toolbar={
                                    <div className="d-flex align-items-center">
                                        <select
                                            className="form-control form-control-sm mr-3"
                                            value={timeSeriesPageSize}
                                            onChange={(e) => setTimeSeriesPageSize(Number(e.target.value))}
                                            style={{ width: 120 }}
                                        >
                                            <option value={7}>7 / page</option>
                                            <option value={14}>14 / page</option>
                                            <option value={30}>30 / page</option>
                                        </select>
                                        <div className="text-muted font-size-sm mr-3">
                                            Page {timeSeriesPage} of {timeSeriesTotalPages}
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-light-primary btn-sm mr-2"
                                            onClick={() => setTimeSeriesPage((p) => Math.max(1, p - 1))}
                                            disabled={timeSeriesPage <= 1}
                                        >
                                            Prev
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-light-primary btn-sm"
                                            onClick={() => setTimeSeriesPage((p) => Math.min(timeSeriesTotalPages, p + 1))}
                                            disabled={timeSeriesPage >= timeSeriesTotalPages}
                                        >
                                            Next
                                        </button>
                                    </div>
                                }
                            />
                            <CardBody>
                                <div className="text-muted mb-4">
                                    Earned and redeemed Gems per period. Net Gems = Earned - Redeemed, and Value uses the active exchange rate.
                                </div>
                                {hasTimeSeriesChartData ? (
                                    <div className="d-flex">
                                        <AxisGuide
                                            maxValue={Math.max(...timeSeriesEarned, ...timeSeriesRedeemed, 0)}
                                            label="Gems"
                                        />
                                        <div style={{ overflowX: "auto", overflowY: "hidden", flex: 1 }}>
                                            <div style={{ minWidth: timeSeriesChartWidth }}>
                                                <ReactApexChart
                                                    options={timeSeriesChartOptions}
                                                    series={timeSeriesChartSeries}
                                                    type="line"
                                                    height={260}
                                                    width={timeSeriesChartWidth}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center text-muted py-5">No data for selected period</div>
                                )}

                                <div className="row mt-6">
                                    <div className="col-md-4 mb-4 mb-md-0">
                                        <div className="d-flex flex-column">
                                            <span className="text-muted font-size-sm mb-1">Total earned</span>
                                            <span className="font-weight-bolder">{timeSeriesTotals.earned.toLocaleString()}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-4 mb-md-0">
                                        <div className="d-flex flex-column">
                                            <span className="text-muted font-size-sm mb-1">Total redeemed</span>
                                            <span className="font-weight-bolder text-danger">{timeSeriesTotals.redeemed.toLocaleString()}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="d-flex flex-column">
                                            <span className="text-muted font-size-sm mb-1">Net</span>
                                            <span className="font-weight-bolder">{timeSeriesTotalNet.toLocaleString()}</span>
                                            <span className="text-muted font-size-sm">
                                                {gemsToCash(timeSeriesTotalNet) !== null ? `≈ ${formatMoney(activeRateCurrency, gemsToCash(timeSeriesTotalNet))}` : "—"}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="table-responsive mt-6">
                                    <table className="table table-sm table-head-custom">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th className="text-right">Earned</th>
                                                <th className="text-right">Redeemed</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pagedTimeSeries.length ? (
                                                <>
                                                    {pagedTimeSeries.map((p) => {
                                                        const earned = Number(p.totalEarned ?? 0);
                                                        const redeemed = Number(p.totalRedeemed ?? 0);
                                                        return (
                                                            <tr key={p.periodKey}>
                                                                <td>{p.periodLabel}</td>
                                                                <td className="text-right">{earned.toLocaleString()}</td>
                                                                <td className="text-right text-danger">{redeemed.toLocaleString()}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                    <tr className="font-weight-bolder">
                                                        <td>Total</td>
                                                        <td className="text-right">{timeSeriesTotals.earned.toLocaleString()}</td>
                                                        <td className="text-right text-danger">{timeSeriesTotals.redeemed.toLocaleString()}</td>
                                                    </tr>
                                                </>
                                            ) : (
                                                <tr>
                                                    <td colSpan={3} className="text-center text-muted py-5">
                                                        No data for selected period
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>

                <div className="row mb-6">
                    <div className="col-12">
                        <Card>
                            <CardHeader title="Reward allocation config" />
                            <CardBody>
                                <div className="text-muted mb-4">
                                    Update the Gems allocated to each reward type and control whether each reward is enabled.
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-sm table-head-custom">
                                        <thead>
                                            <tr>
                                                <th>Reward type</th>
                                                <th>Status</th>
                                                <th className="text-right">Rule value</th>
                                                <th className="text-right">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {isFetchingRewardConfig ? (
                                                <tr>
                                                    <td colSpan={4} className="text-center text-muted py-5">
                                                        Loading reward config...
                                                    </td>
                                                </tr>
                                            ) : rewardConfigList.length ? (
                                                rewardConfigList.map((config) => (
                                                    <tr key={config.rewardType}>
                                                        <td>
                                                            <div className="font-weight-bolder">{config.label}</div>
                                                            <div className="text-muted font-size-sm">
                                                                {config.rewardType}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className={`badge badge-${config.enabled ? "light-success" : "light-danger"}`}>
                                                                {config.enabled ? "Enabled" : "Disabled"}
                                                            </span>
                                                        </td>
                                                        <td className="text-right font-weight-bolder">
                                                            {getRewardConfigSummary(config)}
                                                        </td>
                                                        <td className="text-right">
                                                            <button
                                                                type="button"
                                                                className="btn btn-light-primary btn-sm"
                                                                onClick={() => setSelectedRewardConfig(config)}
                                                            >
                                                                Edit
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={4} className="text-center text-muted py-5">
                                                        No reward config found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>

                <div className="row mb-6">
                    <div className="col-12">
                        <div className="row">
                            {activityCards.map((c) => (
                                <div key={c.label} className="col mb-4">
                                    <Card>
                                        <CardBody>
                                            <div className="d-flex flex-column">
                                                <span className="text-muted font-size-sm mb-2">{c.label}</span>
                                                <span className={`font-weight-bolder font-size-h4 ${c.valueClassName || ""}`}>
                                                    {c.value}
                                                </span>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="row mb-6">
                    <div className="col-12">
                        <Card>
                            <CardHeader title="Top users earning Gems" />
                            <CardBody>
                                <div className="table-responsive">
                                    <table className="table table-sm table-head-custom table-hover">
                                        <thead className="thead-light">
                                            <tr>
                                                <th style={{ width: 60 }}>#</th>
                                                <th>User</th>
                                                <th className="text-right">Total Gems</th>
                                                <th className="text-right">Value</th>
                                                <th className="text-right">Rewards</th>
                                                <th className="text-right">Activity Types</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {topUsers.length ? (
                                                topUsers.map((u, index) => (
                                                    <tr key={u.userId}>
                                                        <td className="text-muted">{index + 1}</td>
                                                        <td>
                                                            {formatUsername(u.userId, u.username || u.userId, "", null, "gems")}
                                                        </td>
                                                        <td className="text-right font-weight-bolder">
                                                            {Number(u.totalGemsEarned ?? 0).toLocaleString()}
                                                        </td>
                                                        <td className="text-right text-muted">
                                                            {gemsToCash(u.totalGemsEarned) !== null
                                                                ? formatMoney(activeRateCurrency, gemsToCash(u.totalGemsEarned))
                                                                : "—"}
                                                        </td>
                                                        <td className="text-right">
                                                            {Number(u.rewardCount ?? 0).toLocaleString()}
                                                        </td>
                                                        <td className="text-right">
                                                            {Number(u.activityTypes ?? 0).toLocaleString()}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={6} className="text-center text-muted py-5">
                                                        No top users for selected period
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>

                <div className="row">
                    <div className="col-12 mb-6">
                        <Card>
                            <CardHeader title="Reports" />
                            <CardBody>
                                <div className="d-flex flex-wrap align-items-end">
                                    <div className="mr-4 mb-3" style={{ minWidth: 160 }}>
                                        <span className="text-muted font-size-sm d-block mb-1">Start date</span>
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="startDate"
                                            value={reportFilters.startDate}
                                            onChange={onReportFilterChange}
                                        />
                                    </div>
                                    <div className="mr-4 mb-3" style={{ minWidth: 160 }}>
                                        <span className="text-muted font-size-sm d-block mb-1">End date</span>
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="endDate"
                                            value={reportFilters.endDate}
                                            onChange={onReportFilterChange}
                                        />
                                    </div>
                                    <div className="mr-4 mb-3" style={{ minWidth: 180 }}>
                                        <span className="text-muted font-size-sm d-block mb-1">Report type</span>
                                        <select
                                            className="form-control"
                                            name="reportType"
                                            value={reportFilters.reportType}
                                            onChange={onReportFilterChange}
                                        >
                                            <option value="overview">overview</option>
                                            <option value="accruals">accruals</option>
                                            <option value="redemptions">redemptions</option>
                                            <option value="campaigns">campaigns</option>
                                            <option value="users">users</option>
                                        </select>
                                    </div>
                                    <div className="mr-4 mb-3" style={{ minWidth: 180 }}>
                                        <span className="text-muted font-size-sm d-block mb-1">Reward type</span>
                                        <select
                                            className="form-control"
                                            name="rewardTypes"
                                            value={reportFilters.rewardTypes?.[0] || ""}
                                            onChange={onReportFilterChange}
                                        >
                                            <option value="">All</option>
                                            {rewardTypeOptions.map((option) => (
                                                <option key={option} value={option}>
                                                    {formatRewardTypeLabel(option)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="mr-4 mb-3" style={{ minWidth: 180 }}>
                                        <span className="text-muted font-size-sm d-block mb-1">Status</span>
                                        <select
                                            className="form-control"
                                            name="statuses"
                                            value={reportFilters.statuses?.[0] || ""}
                                            onChange={onReportFilterChange}
                                        >
                                            <option value="">All</option>
                                            <option value="active">active</option>
                                            <option value="completed">completed</option>
                                            <option value="pending">pending</option>
                                            <option value="failed">failed</option>
                                        </select>
                                    </div>
                                    <div className="mr-4 mb-3" style={{ minWidth: 140 }}>
                                        <span className="text-muted font-size-sm d-block mb-1">Format</span>
                                        <select
                                            className="form-control"
                                            name="format"
                                            value={reportFilters.format}
                                            onChange={onReportFilterChange}
                                        >
                                            <option value="csv">csv</option>
                                            <option value="xlsx">xlsx</option>
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        {/*
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            onClick={onGenerateReport}
                                            disabled={isGeneratingReport}
                                        >
                                            {isGeneratingReport ? "Generating..." : "Generate report"}
                                        </button>
                                        */}
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            onClick={onGenerateReport}
                                            disabled={isGeneratingReport || isDownloadingReport}
                                        >
                                            {(isGeneratingReport || isDownloadingReport) ? "Exporting..." : "Export report"}
                                        </button>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>

            </div>
            <SetExchangeRateModal
                show={showSetExchangeRateModal}
                onHide={() => setShowSetExchangeRateModal(false)}
                userGroup={userGroup}
                activeRate={activeRate}
            />
            <RewardConfigModal
                show={!!selectedRewardConfig}
                onHide={() => setSelectedRewardConfig(null)}
                userGroup={userGroup}
                rewardConfig={selectedRewardConfig}
            />
        </>
    );
};

export default GemsOverview;
